import type { ResetPasswordRequestOptions } from '@auth0/auth0-acul-react/types';
import { useErrors } from '@auth0/auth0-acul-react/reset-password-request';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Input from 'creative-ui/uikit/Input';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import oktaIdentifiantUniqueSvg from '@/assets/okta-identifiant-unique.svg';
import { AuthErrorBanner } from '@/components/AuthErrorBanner/AuthErrorBanner';
import Captcha from '@/components/Captcha';
import { useCaptcha } from '@/components/Captcha/useCaptcha';
import { Flex } from '@/components/Layout/Flex/Flex';
import { useTrackErrors } from '@/hooks/useTrackErrors';
import { useResetPasswordRequestManager } from '@/screens/reset-password-request/hooks/useResetPasswordRequestManager';

export function ResetPasswordRequestForm() {
  const {
    handleResetPasswordRequest,
    inputfield,
    captcha,
    isCaptchaAvailable,
  } = useResetPasswordRequestManager();

  const { captchaConfig, captchaProps, captchaValue } = useCaptcha(
    captcha || undefined,
  );

  const { errors } = useErrors();
  const { generalErrorMessages, fieldErrors } = useTrackErrors(
    errors,
    'recovery/email/error',
  );

  const { t } = useTranslation(['resetPasswordRequest', 'common']);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    clearErrors,
  } = useForm<ResetPasswordRequestOptions>({
    defaultValues: {
      username: String(inputfield),
      captcha: '',
    },
  });

  const onSubmit = async (formData: ResetPasswordRequestOptions) => {
    await handleResetPasswordRequest(
      formData.username,
      isCaptchaAvailable && captchaValue ? captchaValue : undefined,
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gapY="32px">
        <AuthErrorBanner errors={generalErrorMessages} />
        <Controller
          name="username"
          control={control}
          rules={{
            required: t('resetPasswordForm.errorRequired'),
          }}
          render={({ field }) => (
            <Input
              {...field}
              type="email"
              placeholder={t('resetPasswordForm.emailPlaceholder')}
              status={fieldErrors.username ? 'error' : undefined}
              statusMessage={fieldErrors.username}>
              {t('resetPasswordForm.emailLabel')}
            </Input>
          )}
        />

        {isCaptchaAvailable && captchaConfig && (
          <Captcha
            captcha={captchaConfig}
            name="captcha"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            error={fieldErrors.captcha}
            rules={{ required: t('common:captcha.errorRequired') }}
            {...captchaProps}
          />
        )}

        <CtaContainer stack>
          <Cta
            className="c-submitCta"
            type="primary"
            submit
            disabled={isSubmitting}
            isWaiting={isSubmitting}>
            {t('resetPasswordForm.title')}
          </Cta>
        </CtaContainer>

        <img
          src={oktaIdentifiantUniqueSvg}
          alt="okta identifiant unique"
          className="u-margin-t-m"
        />
      </Flex>
    </form>
  );
}
